<script setup lang="ts">
import { useBreadcrumbSchema } from "~/composables/useBreadcrumbSchema";
import { BreadcrumbRoute } from "~/types/breadcrumb-routes";

const WrVideoHeroSection = defineAsyncComponent(() => import('~/components/molecules/WrVideoHeroSection.vue'));
const ServiceValueCard = defineAsyncComponent(() => import('~/components/molecules/ServiceValueCard.vue'));
const WrImageAndCardSection = defineAsyncComponent(() => import('~/components/molecules/WrImageAndCardSection.vue'));
const WrDifferenceSection = defineAsyncComponent(() => import('~/components/molecules/WrDifferenceSection.vue'));
const WrDesignFaqsSection = defineAsyncComponent(() => import('~/components/molecules/WrDesignFaqsSection.vue'));
const WrDesignServiceCardsSection = defineAsyncComponent(() => import('~/components/molecules/WrDesignServiceCardsSection.vue'));
const WrDesignStaggeredProcessCards = defineAsyncComponent(() => import('~/components/molecules/WrDesignStaggeredProcessCards.vue'));
const WrDesignHeroCard = defineAsyncComponent(() => import('~/components/molecules/WrDesignHeroCard.vue'));

const url = useRequestURL();
const { data } = await useFetch(
  `${url.origin}/content/services/mobility/reactnative-app-development-content.json`
);

/* ─── SEO ─── */
const breadcrumbs = useBreadcrumbSchema(BreadcrumbRoute.Services, data.value?.hero?.heading);

useHead({
  title: data.value?.meta_tags?.title || "",
  meta: [
    ...(data.value?.meta_tags?.meta || []),
    { property: "og:title", content: data.value?.meta_tags?.title || "" },
    {
      property: "og:description",
      content:
        (data.value?.meta_tags?.meta as any[]).find(
          (m) => m.name === "description"
        )?.content || "",
    },
    { name: "twitter:card", content: "summary_large_image" },
    {
      name: "twitter:title",
      content: data.value?.meta_tags?.title || "",
    },
    {
      name: "twitter:description",
      content:
        (data.value?.meta_tags?.meta as any[]).find(
          (m) => m.name === "description"
        )?.content || "",
    },
  ],
  link: [
    {
      rel: "preload",
      as: "image",
      href: "/images/services/hero-application-development.webp",
    },
  ],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Service",
            name: data.value?.hero?.heading || "React Native Development Services",
            serviceType: "Mobile App Development",
            url: `${url.origin}/services/mobility/reactnative-app-development`,
            description: data.value?.hero?.subcopy || "React Native app development services for Android and iOS cross-platform mobile applications.",
            provider: {
              "@type": "Organization",
              name: "WebReinvent",
              url: "https://webreinvent.com",
            },
            areaServed: {
              "@type": "Country",
              name: "India",
            },
          },
          {
            "@type": "FAQPage",
            mainEntity: (data.value?.faq?.items || []).map((item) => ({
              "@type": "Question",
              name: item.title,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.description.replace(/<[^>]*>/g, ""),
              },
            })),
          },
          ...(breadcrumbs.value ? [breadcrumbs.value] : []),
        ],
      }),
    },
  ],
});

const projects = ref([
  {
    id: "01",
    title: "Real time",
    tech: "PHP, Laravel, Vue, Tailwind",
  },
  {
    id: "02",
    title: "Order Management",
    tech: "PHP, Laravel, Vue, Tailwind",
  },
  {
    id: "03",
    title: "Analytic",
    tech: "PHP, Laravel, Vue, Tailwind",
  },
  {
    id: "04",
    title: "Leaning Management System",
    tech: "PHP, Laravel, Vue, Tailwind",
  },
  {
    id: "05",
    title: "Live Streaming",
    tech: "PHP, Laravel, Vue, Tailwind",
  },
]);
const processSteps = computed(() =>
  (data.value?.development_process?.steps || []).map((step) => ({
    number: step?.number,
    title: step?.title,
    desc: step?.desc,
  }))
);
const visible = ref(false);

const formPrefilledData = ref(null);

const openModal = (prefilledValue = null) => {
  if (prefilledValue == null) formPrefilledData.value = null;
  else if (prefilledValue.length) formPrefilledData.value = prefilledValue;
  visible.value = true;
};

definePageMeta({
  layout: "services-layout",
});
</script>

<template> 
<main class="min-h-screen px-4 lg:px-0">
    <div class="max-w-screen-xl mx-auto">
      <header class="pt-32">
        <WrDesignHeroCard
          :heading="data?.hero?.heading"
          :subcopy="data?.hero?.subcopy"
          :subcopy-class="data?.hero?.subcopyClass"
          :cta-text="data?.hero?.ctaText"
          :cta-link="null"
          @cta-clicked="openModal"
          :image-src="data?.hero?.imageSrc"
        />
      </header>
        
  <section class="dark:bg-none section-spacing">
     <div class="container-wrapper-xl">
    <WrVideoHeroSection :data="data?.our_promises" />
    </div>
  </section>

  <section class="dark:bg-none section-spacing">
    <div class="container-wrapper-xl">
      <h2 class="lg:text-center my-6">
        <WrGradientText>
          {{ data?.expertise?.heading }}
        </WrGradientText>
      </h2>
      <p
        class="max-w-screen-lg mx-auto lg:text-center lg:text-xl tracking-[0.5px]"
      >
        {{ data?.expertise?.subheading }}
      </p>

      <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-20 mt-12">
        <div v-for="service in data?.expertise?.expertisetext">
          <ServiceValueCard
            :img="service?.image"
            :title="service?.heading"
            :description="service?.text"
          />
        </div>
      </div>
    </div>
  </section>

  <section class="dark:bg-transparent section-spacing">
    <div class="container-wrapper-xl">
      <WrCardCTA
        append-class="max-md:px-4 py-8"
        :description="data?.project_in_mind?.subheading"
        :icon="data?.project_in_mind?.image"
        :title="data?.project_in_mind?.heading"
        :subtitle="data?.project_in_mind?.small_heading"
        :button-text="data?.project_in_mind?.linkText"
      />
    </div>
  </section>

  <section class="dark:bg-none section-spacing">
    <div class="container-wrapper-xl">
      <h2 class="lg:text-center mb-6">
        <WrGradientText>
          {{ data?.framework_benefits?.heading }}
        </WrGradientText>
      </h2>
      <p
        class="max-w-screen-lg mx-auto lg:text-center lg:text-xl tracking-[0.5px]"
      >
        {{ data?.framework_benefits?.subheading }}
      </p>

      <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-20 mt-12">
        <div v-for="service in data?.framework_benefits?.benefit_items">
          <ServiceValueCard
            :img="service?.image"
            :title="service?.title"
            :description="service?.text"
          />
        </div>
      </div>
    </div>
  </section>

  <section class="dark:bg-none">
    <div class="container-wrapper-xl">
      <WrDifferenceSection
        v-if="
          data?.problemSolution &&
          data?.problemSolution?.leftList &&
          data?.problemSolution?.rightList
        "
        :heading="data?.problemSolution?.heading"
        :description="data?.problemSolution?.description"
        :left-list="data?.problemSolution?.leftList"
        :right-list="data?.problemSolution?.rightList"
        :quote="data?.problemSolution?.quote"
        :cta-text="data?.problemSolution?.ctaText"
        @cta-clicked="openModal"
      />
    </div>
  </section>

  <section class="dark:bg-none max-w-7xl mx-auto">
    <WrDesignServiceCardsSection
      v-if="data?.appTypes && data?.appTypes?.cards"
      :heading-line1="data?.appTypes?.headingLine1"
      :heading-line2="data?.appTypes?.headingLine2"
      :subcopy="data?.appTypes?.subcopy"
      :cards="data?.appTypes?.cards"
      :cta-text="data?.appTypes?.ctaText"
      :cta-link="null"
      @cta-clicked="openModal"
    />
  </section>

  <section class="dark:bg-none">
    <WrDesignStaggeredProcessCards
      v-if="data?.development_process?.steps"
      :heading="data?.development_process?.heading"
      :subcopy="data?.development_process?.subheading"
      :steps="processSteps"
      cta-text="Get a Free Consultation"
      @cta-clicked="openModal"
    />
  </section>

  <section class="dark:bg-none section-spacing">
    <div class="container-wrapper-xl">
      <WrImageAndCardSection :data="data?.partnering" />
      <WrGoogleRating />
    </div>
  </section>

  <section class="bg-transparent section-spacing">
    <div
      class="container-wrapper-xl flex max-lg:flex-col justify-between items-center gap-14 lg:gap-32 lg:*:w-1/2"
    >
      <div>
        <h2 class="text-3xl lg:text-4xl mb-2 max-md:mb-4 md:mb-4">
          <WrGradientText
            :text="data?.contact_us_bar2?.heading"
            append-class="pb-0 lg:pb-2 bg-clip-text bg-[linear-gradient(92.15deg,_var(--text-gradient-1)_-1.57%,_var(--text-gradient-2)_30.72%)]"
          />
        </h2>
        <p class="lg:text-xl tracking-[0.5px]">
          {{ data?.contact_us_bar2?.subheading }}
        </p>
      </div>
      <WrCardCTA
        append-class="lg:py-14 lg:mr-8"
        :icon="data?.contact_us_bar2?.box?.image"
        alt="Development Services"
        :subtitle="data?.contact_us_bar2?.box?.small_heading"
        :title="data?.contact_us_bar2?.box?.heading"
        :description="data?.contact_us_bar2?.box?.subheading"
        buttonText="Let's Connect"
      />
    </div>
  </section>

  <section class="dark:bg-none section-spacing">
    <div
      class="container-wrapper-xl flex max-lg:flex-col justify-between gap-14"
    >
      <div class="lg:w-[45%]">
        <h2 class="text-3xl mb-2 max-md:mb-4 md:mb-4">
          <WrGradientText
            :text="data?.hire_us?.heading"
            append-class="py-0 lg:pb-2 bg-clip-text bg-[linear-gradient(92.15deg,_var(--text-gradient-1)_-1.57%,_var(--text-gradient-2)_30.72%)]"
          />
        </h2>
        <p class="text-xl max-md:text-sm tracking-[0.5px]">
          {{ data?.hire_us?.subheading }}
        </p>
      </div>
      <div class="grid sm:grid-cols-2 gap-4">
        <!-- Hourly Basis -->
        <WrCardHiring
          @openModal="openModal"
          v-for="(card, index) in data?.hire_us?.cards"
          :title="card?.type"
          :description="card?.billing"
          :badgeText="card?.duration"
          :offerText="card?.offerText"
          :icon="card?.image"
          :showCounter="card?.showCounter"
          :defaultCount="parseInt(card?.defaultCount)"
          :buttonLabel="card?.buttonLabel"
          :page="{ name: 'React-Native' }"
          :index="index"
        />
      </div>
    </div>
  </section>

  <section class="dark:bg-none section-spacing">
    <div class="container-wrapper-xl">
    <h2 class="lg:text-center">
      <WrGradientText>
        {{ data?.other_services?.side_box?.heading }}
      </WrGradientText>
    </h2>
    <p
      class="max-w-screen-md mx-auto lg:text-lg lg:text-center tracking-[0.5px] mb-5 mt-2"
    >
      {{ data?.other_services?.side_box?.subheading }}
    </p>
    <WrFrameworksCarousel />
    </div>
  </section>

  <section class="dark:bg-none section-spacing">
    <div class="container-wrapper-xl">
      <h2 class="text-center">
        <WrGradientText>
          {{ data?.call_to_action?.heading }}
        </WrGradientText>
      </h2>
      <p class="max-w-screen-lg mx-auto text-center text-xl tracking-[0.5px]">
        {{ data?.call_to_action?.subheading }}
      </p>

      <div class="grid sm:grid-cols-2 gap-14 lg:gap-44 lg:px-12 mt-12">
        <WrCardCTA
          v-for="card in data?.call_to_action?.boxes"
          :img-position="card.imagePosition"
          append-class="pt-36 max-lg:pb-8 lg:py-14 lg:pl-20 pr-8"
          :icon="card.image"
          alt="Development Services"
          :subtitle="card?.small_heading"
          :title="card?.heading"
          :description="card?.subheading"
          :buttonText="card?.linkText"
          :iconAppendClass="!!card?.iconAppend"
          :link="card.link"
        />
      </div>
    </div>
  </section>

  <section
    class="relative z-10 section-spacing dark:bg-none overflow-visible order-9"
  >
    <div class="container-wrapper-lg w-full relative">
      <div
        class="hidden dark:block opacity-30 md:size-[764px] rounded-full bg-[linear-gradient(136.28deg,_rgba(51,4,79,0)_30.43%,_#260049_54.53%,_#36006F_92.37%)] absolute -top-[30%] -left-[67%]"
      ></div>

      <div
        class="hidden dark:block opacity-50 size-[190px] rounded-full bg-[image:var(--bg-circle-top-light)] absolute -bottom-[15%] right-0 top-0"
      ></div>
      <WrSelectedProject />
    </div>
  </section>
  <WrDesignFaqsSection
    v-if="data?.faq && data?.faq?.items"
    :heading="data?.faq?.heading"
    :suffix="data?.faq?.suffix"
    :subtext="data?.faq?.subtext"
    :filter-tags="data?.faq?.filterTags"
    :faq-items="data?.faq?.items"
    :cta-text="data?.faq?.ctaText"
    :cta-link="null"
    @cta-clicked="openModal"
  />

  <section
    class="section-spacing relative  overflow-hidden z-20"
  >
  <div class="container-wrapper-xl"></div>
    <h2 class="text-center">
      <WrGradientText text="Trusted for excellence and efficiency" />
    </h2>
    <p
      class="text-lg text-[var(--foreground)] mb-10 text-center tracking-[0.5px]"
    >
      WebReinvent is trusted by leading organisations to enhance their
      development and operational efficiency.
    </p>
    <!-- Carousel Section -->

    <WrTestimonialCardCarousel />

    <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-2"
      >View All</WrButton
    >
  </section>
  <WrContactFormPopup
    :isOpen="visible"
    @close="visible = false"
    class="z-30"
    :prefilledContent="formPrefilledData"
  />
  </div>
</main>
</template>
<style scoped>
:deep(.hero-image-wrapper) {
  max-width: 500px;
}
</style>
